import {html, nothing, svg, type HTMLTemplateResult} from "lit";
import {styleMap} from "lit/directives/style-map.js";

import {poolColorStyle} from "../pool-color";
import {clamp01, fitLabel, SHORTCUT_FIT, type PickerSkinInput} from "./picker-skin-input";

/** At most this many cards in the fan. */
export const MAX_CARDS = 5;
const CARD_W = 56;
const CARD_H = 84;
/** Up to here (of the eased progress, about a third of the time) the cards shuffle; the rest turns the middle
 *  card, slowly. */
const SHUFFLE_UNTIL = 0.65;
/** How often the fan closes and opens again while shuffling. */
const SHUFFLES = 3;

export interface CardPlace {
    /** Centre of the card. */
    readonly x: number;
    readonly y: number;
    /** Degrees. */
    readonly rotate: number;
}

/** The middle card — the one that is turned. */
export function middleCard(count: number): number {
    return Math.floor(count / 2);
}

/**
 * Where the `count` cards lie: a fan at rest; while shuffling (`progress` up to `SHUFFLE_UNTIL`) it closes and
 * opens again `SHUFFLES` times, the cards changing places on the way (the outer ones cross the middle).
 */
export function fanPositions(count: number, progress: number): CardPlace[] {
    const shuffle = clamp01(progress / SHUFFLE_UNTIL);
    // 1 = fan open, -1 = fan mirrored (cards crossed over); ends at 1 exactly.
    const spread = shuffle >= 1 ? 1 : Math.cos(shuffle * SHUFFLES * 2 * Math.PI);
    const places: CardPlace[] = [];
    for (let i = 0; i < count; i++) {
        const offset = i - (count - 1) / 2;
        places.push({x: 100 + offset * 26 * spread, y: 100 + Math.abs(offset) * 6 * Math.abs(spread), rotate: offset * 9 * spread});
    }
    return places;
}

/** The middle card's turn: its horizontal scale (1 → 0 → 1) and whether its face shows — from
 *  `SHUFFLE_UNTIL` to the end of the draw, ending face up exactly at `progress = 1`. */
export function cardFlip(progress: number): {scaleX: number; faceUp: boolean} {
    const turn = clamp01((progress - SHUFFLE_UNTIL) / (1 - SHUFFLE_UNTIL));
    return {scaleX: Math.abs(Math.cos(turn * Math.PI)), faceUp: turn > 0.5};
}

/** Karten aufdecken: up to five face-down cards in a fan; they shuffle, then the middle one turns over and
 *  shows the result. */
export function renderCardsSkin({pool, current, progress, phase}: PickerSkinInput): HTMLTemplateResult {
    const count = Math.min(MAX_CARDS, pool.length);
    const at = phase === 'shown' ? 1 : phase === 'spinning' ? progress : 0;
    const places = fanPositions(count, at);
    const middle = middleCard(count);
    const flip = phase === 'idle' || current === null ? {scaleX: 1, faceUp: false} : cardFlip(at);
    // The middle card is drawn last, on top of the fan.
    const order = [...places.keys()].filter((i) => i !== middle).concat(middle);

    return html`
        <svg class="picker-cards" viewBox="0 0 200 200" aria-hidden="true">
            ${svg`${order.map((i) => {
                const {x, y, rotate} = places[i];
                const scale = i === middle ? flip.scaleX : 1;
                const faceUp = i === middle && flip.faceUp && current !== null;
                return svg`
                    <g class=${faceUp ? 'picker-cards__card picker-cards__card--face' : 'picker-cards__card'}
                       transform=${`translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${rotate.toFixed(2)}) scale(${scale.toFixed(3)}, 1)`}
                       style=${faceUp ? styleMap(poolColorStyle(current.uid, current.color, current.textColor ?? null)) : nothing}>
                        <rect class="picker-cards__sheet" x=${-CARD_W / 2} y=${-CARD_H / 2} width=${CARD_W} height=${CARD_H} rx="6"></rect>
                        ${faceUp
                            ? svg`
                                <text class="picker-cards__shortcut" x="0" y="-6">${fitLabel(current.shortcut, SHORTCUT_FIT)}</text>
                                <text class="picker-cards__name" x="0" y="24">${fitLabel(current.name, 10)}</text>`
                            : svg`<rect class="picker-cards__pattern" x=${-CARD_W / 2 + 6} y=${-CARD_H / 2 + 6} width=${CARD_W - 12} height=${CARD_H - 12} rx="3"></rect>`}
                    </g>
                `;
            })}`}
        </svg>
    `;
}
